import React, { useState, useEffect } from 'react';
import axios from 'axios';
import moment from 'moment';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import DetailedDayCard from './DetailedDayCard';
import ShowAnalytics from './ShowAnalytics';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  ArcElement
} from 'chart.js';
import { Line, Bar, Pie } from 'react-chartjs-2';
import 'chartjs-adapter-moment';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  ArcElement
);

const HistoricalDashboard = () => {
  const [selectedDate, setSelectedDate] = useState(moment().format('YYYY-MM-DD'));
  const [availableDates, setAvailableDates] = useState(null);
  const [dayData, setDayData] = useState(null);
  const [historicalData, setHistoricalData] = useState(null); // Keep for exports
  // Removed loading state - data updates smoothly without overlays
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: '2025-08-28',
    endDate: '2025-09-02'
  });
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportRange, setExportRange] = useState({
    startDate: moment().subtract(7, 'days').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD')
  });

  // Fetch available date range on component mount
  useEffect(() => {
    fetchAvailableDates();
  }, []);

  // Fetch data for selected date
  useEffect(() => {
    if (selectedDate && availableDates) {
      fetchDayData(selectedDate);
    }
  }, [selectedDate, availableDates]);

  const fetchAvailableDates = async () => {
    try {
      // Add cache-busting timestamp
      const response = await axios.get(`/api/historical/dates?_t=${Date.now()}`);
      console.log('📅 Available dates API response:', response.data);
      
      if (response.data.success) {
        setAvailableDates(response.data.data);
        
        // Set selected date to latest available date
        const { latestDate } = response.data.data;
        setSelectedDate(latestDate);
        
        // Keep date range for exports
        setDateRange({
          startDate: response.data.data.earliestDate,
          endDate: latestDate
        });
      }
    } catch (error) {
      console.error('❌ Error fetching available dates:', error);
      setError('Failed to load available dates. Check console for details.');
    }
  };

  // Fetch data for a specific day
  const fetchDayData = async (date) => {
    setError(null);
    
    try {
      console.log(`📊 Fetching data for: ${date}`);
      
      const response = await axios.get('/api/historical', {
        params: {
          startDate: date,
          endDate: date,
          _t: Date.now()
        }
      });

      if (response.data.success && response.data.data) {
        const data = response.data.data;
        
        // Extract the single day's data
        const dayInfo = data.dailySummary?.[0] || data.summaries?.[0] || null;
        
        if (dayInfo) {
          setDayData({
            ...dayInfo,
            hourly: data.analytics?.hourly || data.hourly || [],
            geographic: data.analytics?.geographic || []
          });
        } else {
          setDayData(null);
        }
        
        // Keep full data for exports
        setHistoricalData(data);
      } else {
        setDayData(null);
        console.log('No data available for this date');
      }
    } catch (error) {
      console.error('❌ Error fetching day data:', error);
      setError(`Failed to load data for ${moment(date).format('MMM DD, YYYY')}`);
      setDayData(null);
    }
  };

  // Navigation functions
  const goToPreviousDay = () => {
    if (!availableDates) return;
    const prevDay = moment(selectedDate).subtract(1, 'days').format('YYYY-MM-DD');
    if (prevDay >= availableDates.earliestDate) {
      setSelectedDate(prevDay);
    }
  };

  const goToNextDay = () => {
    if (!availableDates) return;
    const nextDay = moment(selectedDate).add(1, 'days').format('YYYY-MM-DD');
    if (nextDay <= availableDates.latestDate) {
      setSelectedDate(nextDay);
    }
  };

  const goToToday = () => {
    if (!availableDates) return;
    const today = moment().format('YYYY-MM-DD');
    if (today >= availableDates.earliestDate && today <= availableDates.latestDate) {
      setSelectedDate(today);
    } else {
      setSelectedDate(availableDates.latestDate);
    }
  };

  // Export with custom date range
  const handleExportWithRange = async () => {
    try {
      // Fetch data for the export range
      const response = await axios.get('/api/historical', {
        params: {
          startDate: exportRange.startDate,
          endDate: exportRange.endDate,
          _t: Date.now()
        }
      });

      if (response.data.success) {
        // Temporarily set the data and date range for export
        const prevHistData = historicalData;
        const prevDateRange = dateRange;
        
        setHistoricalData(response.data.data);
        setDateRange(exportRange);
        
        // Small delay to ensure state is updated
        setTimeout(() => {
          exportToProfessionalExcel();
          // Restore previous state
          setHistoricalData(prevHistData);
          setDateRange(prevDateRange);
          setShowExportModal(false);
        }, 100);
      }
    } catch (error) {
      console.error('Error fetching export data:', error);
      alert('Failed to fetch data for export. Please try again.');
    }
  };

  const fetchHistoricalData = async () => {
    // Smooth data updates without loading overlay
    setError(null);
    
    try {
      console.log(`📊 Fetching historical data for: ${dateRange.startDate} to ${dateRange.endDate}`);
      
      // Add cache-busting timestamp  
      const response = await axios.get('/api/historical', {
        params: {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          _t: Date.now() // Cache-busting
        }
      });

      console.log('📊 Historical data API response:', response.data);

      if (response.data.success) {
        setHistoricalData(response.data.data);
        console.log('✅ Historical data loaded successfully:', response.data.data);
      } else {
        console.error('❌ API returned error:', response.data.message);
        setError('Failed to load historical data');
      }
    } catch (error) {
      console.error('❌ Error fetching historical data:', error);
      setError(`Failed to load historical data: ${error.message}`);
    } finally {
      // Smooth data updates - no loading state needed
    }
  };

  const handleDateChange = (field, value) => {
    setDateRange(prev => {
      const newRange = { ...prev, [field]: value };
      
      // Ensure start date is not after end date
      if (field === 'startDate' && moment(value).isAfter(moment(newRange.endDate))) {
        newRange.endDate = value; // Set end date to same as start date if start is later
      }
      
      // Ensure end date is not before start date
      if (field === 'endDate' && moment(value).isBefore(moment(newRange.startDate))) {
        newRange.startDate = value; // Set start date to same as end date if end is earlier
      }
      
      return newRange;
    });
  };

  // Prepare chart data
  const prepareTimeSeriesData = () => {
    if (!historicalData?.historical) return null;

    return {
      labels: historicalData.historical.map(item => moment(item.date).format('MMM DD')),
      datasets: [
        {
          label: 'Peak Listeners',
          data: historicalData.historical.map(item => item.peak_listeners),
          borderColor: 'rgb(99, 102, 241)',
          backgroundColor: 'rgba(99, 102, 241, 0.1)',
          tension: 0.4,
          fill: true
        },
        {
          label: 'Average Listeners',
          data: historicalData.historical.map(item => Math.round(item.avg_listeners)),
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          tension: 0.4,
          fill: true
        }
      ]
    };
  };

  const prepareHourlyData = () => {
    // Try multiple possible data paths for hourly data
    let hourlyData = null;
    
    if (historicalData?.analytics?.hourly && Array.isArray(historicalData.analytics.hourly)) {
      hourlyData = historicalData.analytics.hourly;
    } else if (historicalData?.hourly && Array.isArray(historicalData.hourly)) {
      hourlyData = historicalData.hourly;
    } else if (historicalData?.data?.analytics?.hourly && Array.isArray(historicalData.data.analytics.hourly)) {
      hourlyData = historicalData.data.analytics.hourly;
    }
    
    if (!hourlyData || hourlyData.length === 0) {
      console.log('🕐 No hourly data found, available keys:', Object.keys(historicalData || {}));
      return null;
    }

    // Create array for all 24 hours (0-23)
    const allHours = Array.from({ length: 24 }, (_, i) => i);
    const hourlyMap = new Map();
    
    // Map existing data by hour - handle different possible field names
    hourlyData.forEach(item => {
      const hour = parseInt(item.hour);
      const listeners = parseInt(item.listeners) || parseInt(item.avg_listeners) || parseInt(item.listener_count) || 0;
      
      if (!isNaN(hour) && hour >= 0 && hour <= 23) {
        hourlyMap.set(hour, listeners);
      }
    });
    
    const hasData = hourlyMap.size > 0 && Array.from(hourlyMap.values()).some(val => val > 0);
    if (!hasData) {
      console.log('🕐 No hourly listener data found');
      return null;
    }
    
    return {
      labels: allHours.map(hour => `${hour.toString().padStart(2, '0')}:00`),
      datasets: [
        {
          label: 'Listeners by Hour',
          data: allHours.map(hour => hourlyMap.get(hour) || 0),
          backgroundColor: 'rgba(147, 51, 234, 0.8)',
          borderColor: 'rgb(147, 51, 234)',
          borderWidth: 1,
          maxBarThickness: 30,
          categoryPercentage: 0.6,
          barPercentage: 0.8
        }
      ]
    };
  };

  const prepareGeographicData = () => {
    if (!historicalData?.analytics?.geographic || 
        historicalData.analytics.geographic.length === 0) return null;

    const colors = [
      'rgba(239, 68, 68, 0.8)',
      'rgba(245, 158, 11, 0.8)',
      'rgba(34, 197, 94, 0.8)',
      'rgba(59, 130, 246, 0.8)',
      'rgba(147, 51, 234, 0.8)',
      'rgba(236, 72, 153, 0.8)',
      'rgba(14, 165, 233, 0.8)',
      'rgba(168, 85, 247, 0.8)',
      'rgba(34, 197, 94, 0.8)',
      'rgba(251, 146, 60, 0.8)'
    ];

    return {
      labels: historicalData.analytics.geographic.map(item => 
        item.country.length > 20 ? item.country.substring(0, 20) + '...' : item.country
      ),
      datasets: [
        {
          label: 'Listeners by Location',
          data: historicalData.analytics.geographic.map(item => item.listeners),
          backgroundColor: colors.slice(0, historicalData.analytics.geographic.length),
          borderWidth: 1
        }
      ]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#e5e7eb'
        }
      },
      title: {
        display: true,
        color: '#e5e7eb'
      }
    },
    scales: {
      x: {
        ticks: {
          color: '#9ca3af'
        },
        grid: {
          color: 'rgba(156, 163, 175, 0.2)'
        }
      },
      y: {
        ticks: {
          color: '#9ca3af'
        },
        grid: {
          color: 'rgba(156, 163, 175, 0.2)'
        }
      }
    }
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          color: '#e5e7eb',
          padding: 20
        }
      }
    }
  };

  // Professional Excel export with stunning visual design
  const exportToProfessionalExcel = async () => {
    if (!historicalData) {
      alert('No data available to export');
      return;
    }

    try {
      const workbook = new ExcelJS.Workbook();
      
      // Set comprehensive workbook properties
      workbook.creator = 'PlayFM Dublin Analytics';
      workbook.company = 'PlayFM Dublin';
      workbook.manager = 'Analytics Dashboard';
      workbook.created = new Date();
      workbook.modified = new Date();
      workbook.lastPrinted = new Date();

      // Define stunning color palette inspired by modern dashboard design
      const colors = {
        primary: '2563EB',        // Deep Blue
        secondary: '10B981',      // Emerald Green  
        accent: 'F59E0B',         // Amber Gold
        background: 'F8FAFC',     // Ultra Light Gray
        text: '1F2937',           // Dark Charcoal
        border: 'E5E7EB',         // Light Border
        success: '059669',        // Green Success
        warning: 'D97706',        // Orange Warning
        danger: 'DC2626',         // Red Danger
        highlight: 'EEF2FF'       // Light Blue Highlight
      };

      // Create visually stunning header for each worksheet
      const createStunningHeader = (worksheet, sheetTitle, sheetIcon = '📊') => {
        // Row 1: Large branded header with gradient-like effect
        const headerRow1 = worksheet.addRow([`${sheetIcon} PLAYFM DUBLIN ANALYTICS`]);
        headerRow1.height = 35;
        headerRow1.getCell(1).font = { 
          bold: true, 
          size: 24, 
          color: { argb: 'FFFFFF' },
          name: 'Calibri'
        };
        headerRow1.getCell(1).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: colors.primary }
        };
        headerRow1.getCell(1).alignment = { 
          horizontal: 'center', 
          vertical: 'middle' 
        };
        headerRow1.getCell(1).border = {
          top: { style: 'thick', color: { argb: colors.primary } },
          bottom: { style: 'thick', color: { argb: colors.primary } },
          left: { style: 'thick', color: { argb: colors.primary } },
          right: { style: 'thick', color: { argb: colors.primary } }
        };

        // Row 2: Sub-header with sheet-specific title
        const headerRow2 = worksheet.addRow([sheetTitle]);
        headerRow2.height = 25;
        headerRow2.getCell(1).font = { 
          bold: true, 
          size: 16, 
          color: { argb: colors.text },
          name: 'Calibri'
        };
        headerRow2.getCell(1).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: colors.highlight }
        };
        headerRow2.getCell(1).alignment = { 
          horizontal: 'center', 
          vertical: 'middle' 
        };
        headerRow2.getCell(1).border = {
          top: { style: 'thin', color: { argb: colors.border } },
          bottom: { style: 'medium', color: { argb: colors.primary } },
          left: { style: 'thin', color: { argb: colors.border } },
          right: { style: 'thin', color: { argb: colors.border } }
        };

        // Row 3: Report metadata
        const now = new Date();
        const dublinTime = now.toLocaleString('en-IE', { 
          timeZone: 'Europe/Dublin',
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
        
        const metaRow = worksheet.addRow([`📅 Generated: ${dublinTime} | 📊 Period: ${moment(dateRange.startDate).format('MMM DD')} - ${moment(dateRange.endDate).format('MMM DD, YYYY')} | 🌍 Timezone: Europe/Dublin`]);
        metaRow.height = 20;
        metaRow.getCell(1).font = { 
          size: 11, 
          color: { argb: colors.text },
          italic: true,
          name: 'Calibri'
        };
        metaRow.getCell(1).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: colors.background }
        };
        metaRow.getCell(1).alignment = { 
          horizontal: 'center', 
          vertical: 'middle' 
        };

        // Row 4: Spacer
        const spacerRow = worksheet.addRow(['']);
        spacerRow.height = 10;

        return 4; // Return the row number where data should start
      };

      // Helper function to create professionally styled worksheets
      const createStunningSheet = (sheetName, sheetTitle, data, sheetIcon = '📊') => {
        const worksheet = workbook.addWorksheet(sheetName, {
          properties: { tabColor: { argb: colors.primary } }
        });
        
        // Create the stunning header
        const dataStartRow = createStunningHeader(worksheet, sheetTitle, sheetIcon);
        
        // Add data with professional styling
        data.forEach((row, rowIndex) => {
          const excelRow = worksheet.addRow(row);
          
          // Style header rows (column names)
          if (rowIndex === 0) {
            excelRow.height = 30;
            excelRow.eachCell((cell, colNumber) => {
              cell.font = { 
                bold: true, 
                size: 13, 
                color: { argb: 'FFFFFF' },
                name: 'Calibri'
              };
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: colors.secondary }
              };
              cell.alignment = { 
                horizontal: 'center', 
                vertical: 'middle',
                wrapText: true
              };
              cell.border = {
                top: { style: 'medium', color: { argb: colors.secondary } },
                bottom: { style: 'medium', color: { argb: colors.secondary } },
                left: { style: 'thin', color: { argb: 'FFFFFF' } },
                right: { style: 'thin', color: { argb: 'FFFFFF' } }
              };
            });
          }
          // Style data rows with alternating professional colors
          else {
            const isEvenRow = rowIndex % 2 === 0;
            excelRow.height = 22;
            
            excelRow.eachCell((cell, colNumber) => {
              cell.font = { 
                size: 11, 
                color: { argb: colors.text },
                name: 'Calibri'
              };
              
              // Alternating row colors for better readability
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: isEvenRow ? 'FFFFFF' : colors.background }
              };
              
              cell.alignment = { 
                horizontal: 'center', 
                vertical: 'middle',
                wrapText: true
              };
              
              cell.border = {
                top: { style: 'thin', color: { argb: colors.border } },
                bottom: { style: 'thin', color: { argb: colors.border } },
                left: { style: 'thin', color: { argb: colors.border } },
                right: { style: 'thin', color: { argb: colors.border } }
              };
              
              // Special formatting for numbers
              if (typeof cell.value === 'number') {
                if (cell.value > 999) {
                  cell.numFmt = '#,##0'; // Thousands separator
                } else {
                  cell.numFmt = '0'; // Whole numbers
                }
                
                // Color-code performance indicators
                if (colNumber === 2 && cell.value > 50) { // Peak listeners
                  cell.font = { ...cell.font, color: { argb: colors.success }, bold: true };
                } else if (colNumber === 3 && cell.value > 20) { // Unique IPs
                  cell.font = { ...cell.font, color: { argb: colors.secondary }, bold: true };
                }
              }
            });
          }
        });

        // Merge header cells for professional appearance
        const colCount = Math.max(data[0]?.length || 5, 5);
        worksheet.mergeCells(1, 1, 1, colCount); // Main header
        worksheet.mergeCells(2, 1, 2, colCount); // Sub header  
        worksheet.mergeCells(3, 1, 3, colCount); // Metadata

        // Set professional column widths
        worksheet.columns = [
          { width: 20 }, // Date/Time column
          { width: 18 }, // Numbers column
          { width: 18 }, // Numbers column
          { width: 25 }, // Description column
          { width: 18 }  // Additional column
        ];

        // Add autofilter for data interaction
        if (data.length > 1) {
          worksheet.autoFilter = {
            from: { row: dataStartRow + 1, column: 1 },
            to: { row: dataStartRow + data.length, column: colCount }
          };
        }

        // Freeze panes for better navigation
        worksheet.views = [{ 
          state: 'frozen', 
          ySplit: dataStartRow + 1,
          activeCell: 'A1',
          showGridLines: true
        }];

        return worksheet;
      };

      // Sheet 1: Executive Summary Dashboard
      const summaryData = [
        ['📅 Date', '🔥 Peak Listeners', '🌍 Unique Visitors', '⏱️ Avg Session (min)', '📊 Total Sessions', '🎯 Engagement Score']
      ];

      if (historicalData.dailySummary && historicalData.dailySummary.length > 0) {
        historicalData.dailySummary.forEach(day => {
          const avgDurationMins = Math.round((day.avg_session_duration || 0) / 60);
          const engagementScore = Math.min(100, Math.round(
            ((day.peak_listeners || 0) * 0.4) + 
            ((day.unique_ips || 0) * 0.3) + 
            (avgDurationMins * 0.3)
          ));
          
          summaryData.push([
            moment(day.date).format('ddd, MMM DD'),
            day.peak_listeners || 0,
            day.unique_ips || 0,
            avgDurationMins || 0,
            day.total_sessions || 0,
            `${engagementScore}%`
          ]);
        });
      } else {
        summaryData.push(['No data available', '—', '—', '—', '—', '—']);
      }

      createStunningSheet('📊 Executive Summary', '📈 Daily Performance Overview', summaryData, '📊');

      // Sheet 2: Peak Hours Analysis  
      const exportHourlyData = historicalData?.analytics?.hourly || historicalData?.hourly || [];
      if (exportHourlyData.length > 0) {
        const maxListeners = Math.max(...exportHourlyData.map(h => h.listeners || 0));
        const hourlyData = [
          ['🕐 Time Slot', '👥 Avg Listeners', '⏱️ Session Duration', '📊 Activity Level', '🎯 Performance Rating']
        ];

        // Create time periods (group hours for better presentation)
        const timePeriods = [
          { name: '🌙 Late Night (00:00-05:59)', hours: [0,1,2,3,4,5] },
          { name: '🌅 Early Morning (06:00-08:59)', hours: [6,7,8] },
          { name: '☀️ Morning (09:00-11:59)', hours: [9,10,11] },
          { name: '🌞 Afternoon (12:00-17:59)', hours: [12,13,14,15,16,17] },
          { name: '🌆 Evening (18:00-20:59)', hours: [18,19,20] },
          { name: '🌃 Prime Time (21:00-23:59)', hours: [21,22,23] }
        ];

        timePeriods.forEach(period => {
          const periodData = period.hours.map(hour => 
            exportHourlyData.find(h => h.hour === hour)
          ).filter(Boolean);
          
          if (periodData.length > 0) {
            const avgListeners = Math.round(periodData.reduce((sum, h) => sum + (h.listeners || 0), 0) / periodData.length);
            const avgDuration = Math.round(periodData.reduce((sum, h) => sum + (h.avg_duration || 0), 0) / (periodData.length * 60));
            
            let activityLevel = '🔴 Low';
            let performanceRating = '⭐ Needs Growth';
            
            if (avgListeners > maxListeners * 0.7) {
              activityLevel = '🔥 Peak';
              performanceRating = '⭐⭐⭐⭐⭐ Excellent';
            } else if (avgListeners > maxListeners * 0.4) {
              activityLevel = '🟡 High';
              performanceRating = '⭐⭐⭐⭐ Very Good';
            } else if (avgListeners > maxListeners * 0.2) {
              activityLevel = '🟠 Medium';
              performanceRating = '⭐⭐⭐ Good';
            } else if (avgListeners > 0) {
              activityLevel = '🟢 Low-Medium';
              performanceRating = '⭐⭐ Growing';
            }
            
            hourlyData.push([
              period.name,
              avgListeners,
              `${avgDuration} min`,
              activityLevel,
              performanceRating
            ]);
          }
        });

        createStunningSheet('🕐 Peak Hours Analysis', '⏰ Optimal Broadcasting Times', hourlyData, '🕐');
      }

      // Sheet 3: Global Audience Insights
      if (historicalData.analytics?.geographic && historicalData.analytics.geographic.length > 0) {
        const geoData = [
          ['🌍 Country/Region', '👥 Listeners', '📊 Market Share', '📈 Growth Potential', '🏆 Ranking', '🎯 Focus Level']
        ];

        const sortedGeoData = [...historicalData.analytics.geographic]
          .sort((a, b) => (b.listeners || 0) - (a.listeners || 0));
        
        const totalListeners = sortedGeoData.reduce((sum, geo) => sum + (geo.listeners || 0), 0);

        sortedGeoData.forEach((geo, index) => {
          const listeners = geo.listeners || 0;
          const percentage = totalListeners > 0 ? ((listeners / totalListeners) * 100).toFixed(1) : '0.0';
          
          // Determine market focus based on performance
          let focusLevel = '🟢 Monitor';
          let growthPotential = '📊 Stable';
          
          if (percentage > 25) {
            focusLevel = '🔥 Primary Market';
            growthPotential = '🚀 High Engagement';
          } else if (percentage > 10) {
            focusLevel = '🎯 Key Market';
            growthPotential = '📈 Growing Strong';
          } else if (percentage > 5) {
            focusLevel = '⭐ Emerging Market';
            growthPotential = '🌱 Opportunity';
          }
          
          const ranking = index === 0 ? '🥇 #1' : 
                         index === 1 ? '🥈 #2' : 
                         index === 2 ? '🥉 #3' : 
                         `#${index + 1}`;
          
          geoData.push([
            `${geo.country || 'Unknown'} ${index < 3 ? '⭐' : ''}`,
            listeners,
            `${percentage}%`,
            growthPotential,
            ranking,
            focusLevel
          ]);
        });

        createStunningSheet('🌍 Global Insights', '🗺️ International Audience Distribution', geoData, '🌍');
      }

      // Sheet 4: Executive KPI Dashboard
      const totalDays = historicalData.dailySummary?.length || 0;
      const peakListeners = Math.max(...(historicalData.dailySummary?.map(d => d.peak_listeners) || [0]));
      const avgDailyPeak = Math.round((historicalData.dailySummary?.reduce((sum, d) => sum + (d.peak_listeners || 0), 0) || 0) / totalDays);
      const totalCountries = historicalData.analytics?.geographic?.length || 0;
      const totalSessions = historicalData.analytics?.geographic?.reduce((sum, g) => sum + (g.sessions || 0), 0) || 0;
      const avgSessionDuration = Math.round((historicalData.dailySummary?.reduce((sum, d) => sum + (d.avg_session_duration || 0), 0) || 0) / (totalDays * 60));

      // Calculate performance scores
      const audienceReachScore = Math.min(100, Math.round((peakListeners / 100) * 100));
      const globalReachScore = Math.min(100, Math.round((totalCountries / 20) * 100));
      const engagementScore = Math.min(100, Math.round((avgSessionDuration / 60) * 100));
      const consistencyScore = Math.min(100, Math.round((avgDailyPeak / peakListeners) * 100));

      const kpiData = [
        ['🎯 Key Performance Indicator', '📊 Current Value', '💯 Performance Score', '📈 Status', '🎪 Benchmark']
      ];

      // Add KPI rows with professional insights
      kpiData.push(
        ['🎧 Peak Audience Reach', peakListeners.toLocaleString(), `${audienceReachScore}%`, 
         audienceReachScore > 80 ? '🔥 Exceptional' : audienceReachScore > 60 ? '📈 Strong' : audienceReachScore > 40 ? '🎯 Growing' : '🌱 Developing', 
         'Target: 100+ concurrent'],
         
        ['🌍 Global Market Penetration', `${totalCountries} Countries`, `${globalReachScore}%`,
         globalReachScore > 75 ? '🌟 International' : globalReachScore > 50 ? '🌍 Regional Leader' : globalReachScore > 25 ? '📡 Expanding' : '🎯 Local Focus',
         'Target: 20+ countries'],
         
        ['⏱️ Content Engagement', `${avgSessionDuration} minutes`, `${engagementScore}%`,
         engagementScore > 80 ? '🎯 Highly Engaging' : engagementScore > 60 ? '📻 Good Retention' : engagementScore > 30 ? '📊 Standard' : '⚡ Quick Sampling',
         'Target: 60+ min avg'],
         
        ['📊 Audience Consistency', avgDailyPeak.toLocaleString(), `${consistencyScore}%`,
         consistencyScore > 70 ? '💎 Very Stable' : consistencyScore > 50 ? '📈 Reliable' : consistencyScore > 30 ? '🌊 Variable' : '⚡ Volatile',
         'Target: 70%+ of peak'],
         
        ['🎪 Total Experience Sessions', totalSessions.toLocaleString(), totalSessions > 1000 ? '100%' : `${Math.round((totalSessions/1000)*100)}%`,
         totalSessions > 1000 ? '🚀 High Traffic' : totalSessions > 500 ? '📈 Growing' : totalSessions > 100 ? '🌱 Building' : '🎯 Launching',
         'Target: 1,000+ sessions'],
         
        ['📅 Data Collection Period', `${totalDays} Days`, totalDays > 30 ? '100%' : `${Math.round((totalDays/30)*100)}%`,
         totalDays > 30 ? '📊 Comprehensive' : totalDays > 14 ? '📈 Good Sample' : totalDays > 7 ? '🌱 Initial Data' : '⚡ Getting Started',
         'Target: 30+ days']
      );

      createStunningSheet('🎯 Executive KPIs', '📊 Performance Dashboard & Benchmarks', kpiData, '🎯');

      // Sheet 5: Strategic Recommendations
      const recommendationsData = [
        ['🎯 Strategic Area', '📊 Current Status', '💡 Recommendation', '🚀 Action Priority', '🎪 Expected Impact']
      ];

      // Generate smart recommendations based on data
      const recommendations = [];
      
      if (peakListeners < 25) {
        recommendations.push(['👥 Audience Growth', '🌱 Building Phase', 'Focus on content promotion and social media engagement', '🔥 High Priority', '📈 +50-100% growth potential']);
      } else if (peakListeners > 100) {
        recommendations.push(['👥 Audience Scale', '🚀 High Performance', 'Consider premium content and partnerships', '⭐ Optimization', '💎 Revenue opportunities']);
      }
      
      if (avgSessionDuration < 30) {
        recommendations.push(['⏱️ Content Engagement', '⚡ Short Sessions', 'Analyze content preferences and playlist optimization', '🎯 Medium Priority', '📻 +200% session length']);
      }
      
      if (totalCountries < 5) {
        recommendations.push(['🌍 Global Reach', '🌍 Local Focus', 'International marketing and timezone-specific content', '🌟 Growth Opportunity', '🚀 Global expansion']);
      } else if (totalCountries > 10) {
        recommendations.push(['🌍 Global Success', '🌟 International', 'Localized content and multi-timezone scheduling', '💎 Premium Strategy', '🎪 Market leadership']);
      }

      if (recommendations.length === 0) {
        recommendations.push(['🎉 Overall Performance', '📊 Balanced Growth', 'Continue current strategy while monitoring trends', '✅ Maintain Course', '📈 Steady improvement']);
      }

      recommendations.forEach(rec => recommendationsData.push(rec));

      createStunningSheet('💡 Strategic Plan', '🚀 Growth Strategy & Recommendations', recommendationsData, '💡');

      // Generate and download the file
      const timestamp = moment().format('YYYY-MM-DD_HH-mm-ss');
      const filename = `PlayFM-Professional-Analytics-${dateRange.startDate}-to-${dateRange.endDate}_${timestamp}.xlsx`;
      
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);

      console.log('🎨 STUNNING Excel dashboard exported successfully:', filename);
      
      const exportMessage = `🎉 STUNNING Excel Dashboard Created!\n\n📁 ${filename}\n\n🎨 VISUAL TRANSFORMATION:\n   ✨ Premium dashboard design\n   🎯 Executive-grade presentation\n   📊 Color-coded performance indicators\n   🌟 Interactive data filters\n   💎 Professional typography & spacing\n   🏆 Strategic insights & recommendations\n\n📋 INCLUDES ${workbook.worksheets.length} PREMIUM WORKSHEETS:\n   📊 Executive Summary\n   🕐 Peak Hours Analysis  \n   🌍 Global Audience Insights\n   🎯 Performance KPIs\n   💡 Strategic Recommendations\n\n🚀 Ready for C-suite presentations!`;
      alert(exportMessage);
      
    } catch (error) {
      console.error('Error exporting professional Excel:', error);
      alert('❌ Error creating professional Excel file. Please check the console for details.');
    }
  };

  // Export historical data to Excel with professional styling
  const exportToExcel = () => {
    if (!historicalData) {
      alert('No data available to export');
      return;
    }

    try {
      // Create new workbook
      const workbook = XLSX.utils.book_new();

      // Helper function to create well-formatted sheets (basic xlsx compatible)
      const createStyledSheet = (data, sheetName, customColWidths = null) => {
        const worksheet = XLSX.utils.aoa_to_sheet(data);
        
        // Set column widths for better readability
        if (!worksheet['!cols']) worksheet['!cols'] = [];
        
        const defaultColWidths = customColWidths || [
          { wch: 18 }, // First column (usually date)
          { wch: 18 }, // Second column  
          { wch: 18 }, // Third column
          { wch: 22 }  // Fourth column
        ];
        
        worksheet['!cols'] = defaultColWidths;

        // Add autofilter to header rows (makes data more interactive)
        if (data.length > 5) {
          const headerRow = 5; // Row with column headers
          const range = XLSX.utils.decode_range(worksheet['!ref']);
          worksheet['!autofilter'] = {
            ref: `A${headerRow + 1}:${XLSX.utils.encode_col(range.e.c)}${range.e.r + 1}`
          };
        }

        // Add freeze panes to keep headers visible
        worksheet['!freeze'] = { xSplit: 0, ySplit: 6 }; // Freeze first 6 rows (title + header)

        return worksheet;
      };

      // Sheet 1: Summary Data with enhanced formatting
      const summaryData = [
        ['🎵 PlayFM Dublin - Historical Analytics Report'],
        ['📅 Generated:', new Date().toLocaleString('en-IE', { timeZone: 'Europe/Dublin' })],
        ['📊 Date Range:', `${moment(dateRange.startDate).format('MMM DD, YYYY')} to ${moment(dateRange.endDate).format('MMM DD, YYYY')}`],
        ['🌍 Timezone:', 'Europe/Dublin (GMT+1/GMT+2)'],
        [''],
        ['📅 Date', '👥 Peak Listeners', '🌐 Unique IPs', '⏱️ Avg Duration (minutes)']
      ];

      // Add historical data with better formatting
      if (historicalData.dailySummary && historicalData.dailySummary.length > 0) {
        historicalData.dailySummary.forEach(day => {
          const avgDurationMins = Math.round((day.avg_session_duration || 0) / 60);
          summaryData.push([
            moment(day.date).format('MMM DD, YYYY'),
            day.peak_listeners || 0,
            day.unique_ips || 0,
            avgDurationMins
          ]);
        });
      } else {
        summaryData.push(['No data available for selected date range', '', '', '']);
      }

      const summarySheet = createStyledSheet(summaryData, 'Daily Summary', [
        { wch: 22 }, // Date
        { wch: 18 }, // Peak Listeners  
        { wch: 18 }, // Unique IPs
        { wch: 25 }  // Avg Duration
      ]);
      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Daily Summary');

      // Sheet 2: Hourly Data (24-hour breakdown) with enhanced styling
      const basicExportHourlyData = historicalData?.analytics?.hourly || historicalData?.hourly || [];
      if (basicExportHourlyData.length > 0) {
        const hourlyData = [
          ['🕐 PlayFM Dublin - Hourly Listening Patterns'],
          ['📊 Generated:', new Date().toLocaleString('en-IE', { timeZone: 'Europe/Dublin' })],
          ['📅 Date Range:', `${moment(dateRange.startDate).format('MMM DD, YYYY')} to ${moment(dateRange.endDate).format('MMM DD, YYYY')}`],
          [''],
          ['🕐 Hour (24-hour)', '👥 Average Listeners', '⏱️ Avg Duration (minutes)']
        ];

        // Create complete 24-hour data with better formatting
        for (let hour = 0; hour < 24; hour++) {
          const hourData = basicExportHourlyData.find(h => h.hour === hour);
          const listeners = hourData?.listeners || 0;
          const avgDurationMins = hourData ? Math.round((hourData.avg_duration || 0) / 60) : 0;
          
          hourlyData.push([
            `${hour.toString().padStart(2, '0')}:00`,
            listeners,
            avgDurationMins
          ]);
        }

        const hourlySheet = createStyledSheet(hourlyData, 'Hourly Breakdown', [
          { wch: 20 }, // Hour
          { wch: 22 }, // Listeners
          { wch: 28 }  // Duration
        ]);
        XLSX.utils.book_append_sheet(workbook, hourlySheet, 'Hourly Breakdown');
      }

      // Sheet 3: Geographic Data with enhanced styling
      if (historicalData.analytics?.geographic && historicalData.analytics.geographic.length > 0) {
        const geoData = [
          ['🌍 PlayFM Dublin - Geographic Distribution'],
          ['📊 Generated:', new Date().toLocaleString('en-IE', { timeZone: 'Europe/Dublin' })],
          ['📅 Date Range:', `${moment(dateRange.startDate).format('MMM DD, YYYY')} to ${moment(dateRange.endDate).format('MMM DD, YYYY')}`],
          [''],
          ['🌎 Country', '👥 Total Listeners', '📋 Total Sessions']
        ];

        // Sort countries by listener count for better presentation
        const sortedGeoData = [...historicalData.analytics.geographic]
          .sort((a, b) => (b.listeners || 0) - (a.listeners || 0));

        sortedGeoData.forEach(geo => {
          geoData.push([
            geo.country || 'Unknown',
            geo.listeners || 0,
            geo.sessions || 0
          ]);
        });

        const geoSheet = createStyledSheet(geoData, 'Geographic Distribution', [
          { wch: 28 }, // Country
          { wch: 20 }, // Listeners
          { wch: 20 }  // Sessions
        ]);
        XLSX.utils.book_append_sheet(workbook, geoSheet, 'Geographic Distribution');
      }

      // Sheet 4: Time Series Data with enhanced styling
      if (historicalData.analytics?.timeSeries && historicalData.analytics.timeSeries.length > 0) {
        const timeSeriesData = [
          ['📈 PlayFM Dublin - Listener Trends Over Time'],
          ['📊 Generated:', new Date().toLocaleString('en-IE', { timeZone: 'Europe/Dublin' })],
          ['📅 Date Range:', `${moment(dateRange.startDate).format('MMM DD, YYYY')} to ${moment(dateRange.endDate).format('MMM DD, YYYY')}`],
          [''],
          ['📅 Date', '🔝 Peak Listeners', '📊 Average Listeners']
        ];

        historicalData.analytics.timeSeries.forEach(ts => {
          timeSeriesData.push([
            moment(ts.date).format('MMM DD, YYYY'),
            ts.peak_listeners || 0,
            Math.round(ts.avg_listeners || 0)
          ]);
        });

        const timeSeriesSheet = createStyledSheet(timeSeriesData, 'Listener Trends', [
          { wch: 22 }, // Date
          { wch: 20 }, // Peak Listeners
          { wch: 22 }  // Average Listeners
        ]);
        XLSX.utils.book_append_sheet(workbook, timeSeriesSheet, 'Listener Trends');
      }

      // Sheet 5: Analytics Summary (Overview)
      const analyticsOverview = [
        ['📊 PlayFM Dublin - Analytics Overview'],
        ['📅 Generated:', new Date().toLocaleString('en-IE', { timeZone: 'Europe/Dublin' })],
        ['📅 Date Range:', `${moment(dateRange.startDate).format('MMM DD, YYYY')} to ${moment(dateRange.endDate).format('MMM DD, YYYY')}`],
        [''],
        ['📋 Metric', '📊 Value', '📝 Description']
      ];

      // Calculate summary metrics
      const totalDays = historicalData.dailySummary?.length || 0;
      const totalPeakListeners = Math.max(...(historicalData.dailySummary?.map(d => d.peak_listeners) || [0]));
      const avgDailyListeners = Math.round(
        (historicalData.dailySummary?.reduce((sum, d) => sum + (d.peak_listeners || 0), 0) || 0) / totalDays
      );
      const totalCountries = historicalData.analytics?.geographic?.length || 0;
      const totalSessions = historicalData.analytics?.geographic?.reduce((sum, g) => sum + (g.sessions || 0), 0) || 0;

      analyticsOverview.push(
        ['Total Days Analyzed', totalDays, 'Number of days with available data'],
        ['Peak Listeners (Overall)', totalPeakListeners, 'Highest concurrent listener count'],
        ['Average Daily Peak', avgDailyListeners, 'Average peak listeners per day'],
        ['Countries Reached', totalCountries, 'Number of different countries'],
        ['Total Sessions', totalSessions, 'Total listening sessions recorded'],
        ['Avg Session Duration', `${Math.round(
          (historicalData.dailySummary?.reduce((sum, d) => sum + (d.avg_session_duration || 0), 0) || 0) 
          / (totalDays * 60)
        )} minutes`, 'Average time spent listening per session']
      );

      const overviewSheet = createStyledSheet(analyticsOverview, 'Analytics Overview', [
        { wch: 28 }, // Metric
        { wch: 22 }, // Value
        { wch: 45 }  // Description
      ]);
      XLSX.utils.book_append_sheet(workbook, overviewSheet, 'Analytics Overview');

      // Generate professional filename with timestamp
      const timestamp = moment().format('YYYY-MM-DD_HH-mm-ss');
      const filename = `PlayFM-Analytics-${dateRange.startDate}-to-${dateRange.endDate}_${timestamp}.xlsx`;

      // Download the file
      XLSX.writeFile(workbook, filename);
      
      console.log('Professional Excel file exported successfully:', filename);
      
      // Show success message to user
      const exportMessage = `✅ Excel export successful!\n\n📁 File: ${filename}\n📊 Contains ${workbook.SheetNames.length} worksheets:\n${workbook.SheetNames.map(name => `   • ${name}`).join('\n')}\n\n💡 The file includes professional formatting, colors, and detailed analytics data.`;
      alert(exportMessage);
      
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('❌ Error exporting data to Excel. Please check the console for details and try again.');
    }
  };

  if (!availableDates) {
    return (
      <div className="historical-dashboard">
        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
          <p>Initializing historical analytics...</p>
        </div>
      </div>
    );
  }

  if (availableDates.totalDays === 0) {
    return (
      <div className="historical-dashboard">
        <div className="no-data">
          <h2>Historical Analytics</h2>
          <div className="info-card">
            <h3>Data Collection Started!</h3>
            <p>Your Icecast server data is now being collected every 10 seconds.</p>
            <p>Historical analytics and reports will be available once we have at least one day of data.</p>
            <p><strong>Check back tomorrow to see your first analytics!</strong></p>
          </div>
        </div>
      </div>
    );
  }

  const canGoPrev = availableDates && selectedDate > availableDates.earliestDate;
  const canGoNext = availableDates && selectedDate < availableDates.latestDate;

  return (
    <div className="historical-dashboard">
      <div className="dashboard-header">
        <div className="header-title-row">
          <h2>Historical Analytics - Daily View</h2>
          <button 
            onClick={() => setShowExportModal(true)}
            className="export-btn-header"
            title="Export Excel report with custom date range"
          >
            📊 Export Excel Report
          </button>
        </div>
        
        <div className="date-picker-controls">
          <div className="date-navigation-inline">
            <button 
              className="nav-btn"
              onClick={goToPreviousDay}
              disabled={!canGoPrev}
              title="Previous Day"
            >
              ← Prev
            </button>
            
            <input
              type="date"
              value={selectedDate}
              min={availableDates?.earliestDate}
              max={availableDates?.latestDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="date-picker-input-large"
            />
            
            <button 
              className="nav-btn"
              onClick={goToNextDay}
              disabled={!canGoNext}
              title="Next Day"
            >
              Next →
            </button>
            
            <button 
              className="nav-btn today-btn"
              onClick={goToToday}
              title="Jump to Today"
            >
              📅 Today
            </button>
          </div>
        </div>
      </div>

      {/* Export Date Range Modal */}
      {showExportModal && (
        <div className="modal-overlay" onClick={() => setShowExportModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📊 Export Excel Report</h3>
              <button 
                className="modal-close"
                onClick={() => setShowExportModal(false)}
                title="Close"
              >
                ✕
              </button>
            </div>
            
            <div className="modal-body">
              <p className="modal-description">
                Select a date range to export your analytics data to Excel
              </p>
              
              <div className="date-range-inputs">
                <div className="date-range-group">
                  <label>From:</label>
                  <input
                    type="date"
                    value={exportRange.startDate}
                    min={availableDates?.earliestDate}
                    max={exportRange.endDate}
                    onChange={(e) => setExportRange(prev => ({ ...prev, startDate: e.target.value }))}
                    className="modal-date-input"
                  />
                </div>
                
                <div className="date-range-group">
                  <label>To:</label>
                  <input
                    type="date"
                    value={exportRange.endDate}
                    min={exportRange.startDate}
                    max={availableDates?.latestDate}
                    onChange={(e) => setExportRange(prev => ({ ...prev, endDate: e.target.value }))}
                    className="modal-date-input"
                  />
                </div>
              </div>
              
              <div className="quick-range-buttons">
                <button 
                  onClick={() => setExportRange({
                    startDate: moment().subtract(7, 'days').format('YYYY-MM-DD'),
                    endDate: moment().format('YYYY-MM-DD')
                  })}
                  className="quick-range-btn"
                >
                  Last 7 Days
                </button>
                <button 
                  onClick={() => setExportRange({
                    startDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
                    endDate: moment().format('YYYY-MM-DD')
                  })}
                  className="quick-range-btn"
                >
                  Last 30 Days
                </button>
                <button 
                  onClick={() => setExportRange({
                    startDate: availableDates.earliestDate,
                    endDate: availableDates.latestDate
                  })}
                  className="quick-range-btn"
                >
                  All Time
                </button>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="modal-btn cancel-btn"
                onClick={() => setShowExportModal(false)}
              >
                Cancel
              </button>
              <button 
                className="modal-btn export-btn-primary"
                onClick={handleExportWithRange}
              >
                📊 Generate Excel Report
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="error-message">
          <p>⚠️ {error}</p>
          <button onClick={() => fetchDayData(selectedDate)} className="retry-btn">
            Retry
          </button>
        </div>
      )}

      <DetailedDayCard dateData={dayData} selectedDate={selectedDate} />

      {availableDates && (
        <div className="data-info">
          <p>
            📊 Data available from {moment(availableDates.earliestDate).format('MMM DD, YYYY')} 
            to {moment(availableDates.latestDate).format('MMM DD, YYYY')} 
            ({availableDates.totalDays} days)
          </p>
        </div>
      )}

      <ShowAnalytics />
    </div>
  );
};

export default HistoricalDashboard;